import { RedirectLoginOptions, useAuth0, User } from '@auth0/auth0-react';
import produce from 'immer';
import { useRouter } from 'next/dist/client/router';
import React, { ComponentType, createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PlainRouterQuery, ReactChildrenProps } from 'types/react.types';
import {
    CombinedError,
    OperationContext,
    RequestPolicy,
    TypedDocumentNode,
    useMutation,
    UseMutationResponse,
    useQuery,
    UseQueryArgs,
} from 'urql';
import { DocumentNode } from 'graphql';
import { getClientEnv } from './clientEnv';

import { Espin_Role_Enum } from '../generated/spin-graphql';
import { useAppState } from './providers/AppProvider';
import { Reducer, useImmerReducer } from 'use-immer';

import { GenericInLayoutPageLoading } from '../components/common/GenericFullPageLoading';
import { useIsClientReady } from '../utils/ClientOnly';

export function useRouterQuery<T = PlainRouterQuery>() {
    return (useRouter().query as unknown) as T;
}

// generic hook to load page data w/ fallback to baked data
export function usePageData<T, V = any>(query: DocumentNode, data: T, variables?: V) {
    const ready = useIsClientReady();
    const [result] = useQuery<T, V>({ query, variables });

    if (!ready) {
        return { data, fetching: false, error: undefined };
    }
    if (ready && result.fetching) {
        return { data, fetching: false, error: undefined };
    }
    return result;
}

// Client side restrictions on a component
export function usePageRequireRole({ redirectUrl = '/', roles }: { roles: Espin_Role_Enum[]; redirectUrl?: string }) {
    const { user, isLoggingIn } = useAppState();
    const router = useRouter();
    useEffect(() => {
        if (!isLoggingIn && !!!user) {
            // then this is not a valid user?
            router.push(redirectUrl);
        }
        if (!isLoggingIn && !!user) {
            // valid user, does it have the same role?
            if (!roles.includes(user.role)) {
                router.push(redirectUrl);
            }
        }
    }, [isLoggingIn, redirectUrl, roles, router, user]);
}

export function usePageRequireAuth({ redirectUrl = '/' }: { redirectUrl?: string }) {
    const { user, isLoggingIn } = useAppState();
    const router = useRouter();
    useEffect(() => {
        if (!isLoggingIn && !!!user) {
            // then this is not a valid user?
            router.push(redirectUrl);
        }
    }, [isLoggingIn, redirectUrl, router, user]);
}

type AuthenticatedQueryReturn<T> = {
    isReady: boolean;
    data?: T;
    refetch: (opts?: Partial<OperationContext>) => void;
    isError?: CombinedError;
    fetching: boolean;
    extensions?: Record<string, any>;
};

// Used for any elevated access query
export function useAuthenticatedQuery<Data = any, Variables = object>(
    args: UseQueryArgs<Variables, Data>,
    additionalTypenames: string[] = [],
): AuthenticatedQueryReturn<Data> {
    const [accessToken, setAccessToken] = useState('');

    const [additionalTypes] = useState([...additionalTypenames]);
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    useEffect(() => {
        if (isAuthenticated) {
            (async () => {
                setAccessToken(await getAccessTokenSilently());
            })();
        }
    }, [getAccessTokenSilently, isAuthenticated]);
    const fetchOptions = useMemo(() => {
        if (accessToken !== '') {
            return {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            };
        }
        return {};
    }, [accessToken]);

    const [result, refetch] = useQuery({
        query: args.query,
        pause: (args.pause ?? false) || (isAuthenticated && accessToken === ''),
        variables: args.variables,
        // allow policy override
        requestPolicy: args.requestPolicy ?? 'cache-and-network',
        context: useMemo(
            () => ({
                url: getClientEnv().NEXT_PUBLIC_GQL_URL,
                fetchOptions,
                additionalTypenames: additionalTypes,
            }),
            [additionalTypes, fetchOptions],
        ),
    });

    return {
        data: result.data,
        refetch,
        fetching: result.fetching,
        isReady: !!result.data && !result.fetching,
        isError: result.error,
        extensions: result.extensions,
    };
}

export function useAuthenticatedMutation<Data = any, Variables = object>(
    query: DocumentNode | TypedDocumentNode<Data, Variables> | string,
): UseMutationResponse<Data, Variables> {
    const { getAccessTokenSilently } = useAuth0();
    const [mutationResult, runMutation] = useMutation(query);

    const executeMutation = useCallback(
        async (variables?: Variables, context?: Partial<OperationContext>) => {
            const accessToken = await getAccessTokenSilently();

            return runMutation(variables, {
                ...context,
                url: getClientEnv().NEXT_PUBLIC_GQL_URL,
                requestPolicy: 'network-only' as RequestPolicy,
                fetchOptions: {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            });
        },
        [getAccessTokenSilently, runMutation],
    );

    return [mutationResult, executeMutation];
}

export function withHasuraRoleCheck<P extends object>(
    Component: ComponentType<P>,
    options: WithAuthorizationRequiredProps = { claimCheck: () => true },
    rolesRequired?: Array<string>,
): FC<P> {
    return withAuthorizationRequired(
        Component,
        produce(options, (draft) => {
            if (rolesRequired?.length > 0) {
                draft.claimCheck = (claims: User) => {
                    const hasuraClaims = claims['https://hasura.io/jwt/claims'];
                    if (!hasuraClaims) {
                        return false;
                    }
                    const allowedRoles = hasuraClaims['x-hasura-allowed-roles'];
                    if (!allowedRoles) {
                        return false;
                    }

                    if (allowedRoles.some((role) => rolesRequired.includes(role))) {
                        return true;
                    }
                    return false;
                };
            }
            return draft;
        }),
    );
}

const defaultOnRedirecting = (): JSX.Element => <>Loading....</>;

interface WithAuthorizationRequiredProps {
    returnTo?: string | (() => string);
    onRedirecting?: () => JSX.Element;
    loginOptions?: RedirectLoginOptions;
    claimCheck?: (claims?: User) => boolean;
    unauthorizedComponent?: JSX.Element;
}

export const withAuthorizationRequired = <P extends object>(
    Component: ComponentType<P>,
    options: WithAuthorizationRequiredProps = { claimCheck: () => true },
): FC<P> => {
    return function WithAuthenticationRequired(props: P): JSX.Element {
        const router = useRouter();
        const { user, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
        const {
            returnTo = '/',
            onRedirecting = defaultOnRedirecting,
            loginOptions = {},
            claimCheck = (): boolean => true,
            unauthorizedComponent = <div>Not allowed!</div>,
        } = options;

        // we are specifically looking for AUTHORIZATION, not just auth
        // authorization is based on a role array from auth0
        const routeIsAuthorized = isAuthenticated && claimCheck(user);
        const loggedInButNotAuthorized = isAuthenticated && !claimCheck(user);
        useEffect(() => {
            if (isLoading || routeIsAuthorized || loggedInButNotAuthorized) {
                return;
            }
            const opts = {
                ...loginOptions,
                appState: {
                    ...loginOptions.appState,
                    returnTo: typeof returnTo === 'function' ? returnTo() : returnTo,
                },
            };
            (async (): Promise<void> => {
                await loginWithRedirect(opts);
            })();
        }, [isLoading, routeIsAuthorized, loggedInButNotAuthorized, loginWithRedirect, loginOptions, returnTo]);
        if (loggedInButNotAuthorized) {
            return unauthorizedComponent;
        }
        return routeIsAuthorized ? <Component {...props} /> : onRedirecting();
    };
};

// global search type
export type SearchActionType<FilterType = any> =
    | { type: 'searchData'; value: string; filter?: FilterType; useFilter: boolean }
    | {
          type: 'asyncSearchCompleted';
          results: any;
          totalResults?: number;
          lastSearchTerm: string;
          filtersActive: boolean;
      }
    | {
          type: 'asyncLoadMoreCompleted';
          results: any;
          totalResults?: number;
          lastSearchTerm: string;
          filtersActive: boolean;
          offset: number;
      }
    | { type: 'loadMore' }
    | { type: 'clearSearchData' };
export type SearchBarContextType<SearchDataType = any> = {
    data: null | SearchDataType[];
    offset: number;
    hasSearched: boolean; // for history only + colleges
    hasMore: boolean;
    lastSearchText: string;
    lastSearchFiltersActive: boolean;
    dispatch: (action: SearchActionType) => void;
};

export function createSearchBarContext<SearchDataType = any>() {
    return createContext<SearchBarContextType<SearchDataType>>({
        data: [],
        offset: 0,
        dispatch: null,
        hasMore: false,
        hasSearched: false,
        lastSearchText: '',
        lastSearchFiltersActive: false,
    });
}

export function createSearchBarProvider<SearchDataType = any>(
    SearchContext: React.Context<SearchBarContextType<SearchDataType>>,
) {
    return function SearchBarProvider({
        initialData,
        children,
        reducer,
    }: {
        initialData: {
            offset: number;
            searchData: SearchDataType[];
            fullData: SearchDataType[];
            hasMore: boolean;
            hasSearched: boolean;
            lastSearchText: string;
            lastSearchFiltersActive: boolean;
        };
        reducer: Reducer<{
            offset: number;
            searchData: SearchDataType[];
            fullData: SearchDataType[];
            hasMore: boolean;
            hasSearched: boolean;
            lastSearchFiltersActive: boolean;
            lastSearchText: string;
        }>;
    } & ReactChildrenProps) {
        const [reducerData, dispatch] = useImmerReducer<{
            offset: number;
            searchData: SearchDataType[];
            fullData: SearchDataType[];
            hasMore: boolean;
            hasSearched: boolean;
            lastSearchFiltersActive: boolean;
            lastSearchText: string;
        }>(reducer, initialData);
        const { hasMore, offset, hasSearched, searchData, lastSearchText, lastSearchFiltersActive } = reducerData;
        return (
            <SearchContext.Provider
                value={{
                    offset,
                    hasSearched,
                    data: searchData,
                    dispatch,
                    hasMore,
                    lastSearchText,
                    lastSearchFiltersActive,
                }}
            >
                {children}
            </SearchContext.Provider>
        );
    };
}

export const SearchBarContext = createSearchBarContext();

export function useIsFirstRender() {
    const [isFirst, setIsFirst] = useState(true);

    useEffect(() => {
        setIsFirst(false);
    }, []);
    return isFirst;
}

// Poor man's suspense....
export function SuspendUntilReady({ fallback, children }: { fallback: JSX.Element; children: React.ReactNode }) {
    const isReady = usePageDataReady();
    if (!isReady) {
        return <>{fallback}</>;
    }
    return <>{children}</>;
}

// available wherever
export const PageReadyContext = createContext<boolean>(false);

// provider creator
export function PageDataReadyProvider({ isReady, children }: { isReady: boolean; children: React.ReactNode }) {
    return <PageReadyContext.Provider value={isReady}>{children}</PageReadyContext.Provider>;
}

export function usePageDataReady() {
    return useContext(PageReadyContext);
}

// use at top of pages where profile NEEDS to be completed!
export function useNeedsOnboardingInfoWithRedirect() {
    const { user, isLoggedIn } = useAppState();
    const { loading, needs } = useNeedsOnboardingInfo();
    const router = useRouter();
    useEffect(() => {
        if (!loading) {
            if (needs) {
                router.push('/dashboard/onboarding');
            }
        }
    }, [isLoggedIn, loading, needs, router, user]);
}

export function useNeedsOnboardingInfo() {
    const { user, isLoggedIn, isLoggingIn } = useAppState();
    const [needs, setNeeds] = useState(false);

    useEffect(() => {
        if (isLoggedIn) {
            if (user) {
                if (user.role === Espin_Role_Enum.EspinPlayerUser) {
                    // do we have a spin username and have we acknowledge the TOS + stuff
                    if (!!!user.player_profile?.spin_username) {
                        setNeeds(true);
                    } else {
                        const { ack_terms, ack_waiver, ack_displayed } = user?.player_profile ?? {};
                        if (!!!ack_terms || !!!ack_waiver || !!!ack_displayed) {
                            setNeeds(true);
                        }
                    }
                } else if (user.role === Espin_Role_Enum.EspinCollegeUser) {
                    if (!!!user.college_profile?.college?.id) {
                        setNeeds(true);
                    }
                } else if (user.role === Espin_Role_Enum.EspinParentUser) {
                    if (
                        !!!user.parent_profile?.id ||
                        (user.parent_profile.first_name === '' && user.parent_profile.last_name === '')
                    ) {
                        setNeeds(true);
                    }
                } else if (user.role === Espin_Role_Enum.EspinHighschoolUser) {
                    if (!!!user.high_school_profile?.high_school.id) {
                        setNeeds(true);
                    }
                }
            }
        }
    }, [isLoggedIn, user]);

    return {
        loading: isLoggingIn,
        needs,
    };
}

export function NeedsOnboardingInformation({ children }: { children: React.ReactNode }) {
    const { loading, needs } = useNeedsOnboardingInfo();
    const router = useRouter();

    if (loading) {
        return <GenericInLayoutPageLoading />;
    }
    if (needs) {
        router.push('/dashboard/onboarding');
        return <GenericInLayoutPageLoading />;
    }
    return <>{children}</>;
}
